import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import moment from 'moment';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const AllRequests = () => {
  const [userData, setUserData] = useState(null);
  const [arRequests, setArRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [error, setError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const currentUserId = localStorage.getItem('id');

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then(res => setUserData(res.data))
        .catch(console.error);
    }
  }, []);

  useEffect(() => {
    axiosInstance.get('/attendance-regularization-requests/')
      .then(res => setArRequests(res.data))
      .catch(console.error);

    axiosInstance.get('/assigned-employees/')
      .then(res => setUsers(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRoles([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: roles[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const handleSelectRequest = (e, requestId) => {
    const checked = e.target.checked;
    setSelectedRequests((prev) => {
      if (checked) {
        return prev.includes(requestId) ? prev : [...prev, requestId];
      }
      return prev.filter((id) => id !== requestId);
    });
  };

  const handleSelectAll = (e) => {
    const shouldSelect = e.target.checked;
    
    // Use the same filtering logic as the render
    const filtered = arRequests.filter(request => {
      if (selectedUser) {
        const user = users.find(u => u.id === parseInt(selectedUser));
        if (user && request.employee_code !== user.employee_code) return false;
      }
      if (filterType && request.status !== filterType) return false;
      return true;
    });
    
    if (shouldSelect) {
      setSelectedRequests(filtered.map((req) => req.id));
    } else {
      setSelectedRequests([]);
    }
  };

  const handleBulkAction = (action) => {
    const bulkRequestData = {
      bulk: true,
      status: action === 'approve' ? 'approved' : 'rejected',
      request_ids: selectedRequests,
    };

    axiosInstance.post(`/attendance/regularization/decision/`, bulkRequestData)
      .then(() => {
        setArRequests(arRequests.map(r => 
          selectedRequests.includes(r.id) ? { ...r, status: action === 'approve' ? 'approved' : 'rejected' } : r
        ));
        setSelectedRequests([]);
      })
      .catch(err => console.error(`Error during bulk ${action}:`, err));
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const filteredRequests = arRequests.filter(request => {
    if (selectedUser) {
      const user = users.find(u => u.id === parseInt(selectedUser));
      if (user && request.employee_code !== user.employee_code) return false;
    }
    if (filterType && request.status !== filterType) return false;
    return true;
  });

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={userData} 
        activeRole={userData?.role?.name} 
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>📋 AR Requests</h1>
          <p>Manage Attendance Regularization requests</p>
        </div>

        <div className="dashboard-card full-width">
          <div className="card-header">
            <span className="card-icon">🔔</span>
            <h3>Need To Act</h3>
          </div>
          <div className="card-body">
            <div className="filter-bar">
              <select value={filterType} onChange={e => setFilterType(e.target.value)} className="filter-select">
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} className="filter-select">
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.first_name} ({user.employee_code})</option>
                ))}
              </select>
              <div className="select-all-wrapper">
                <input
                  type="checkbox"
                  id="selectAll"
                  onChange={handleSelectAll}
                  checked={selectedRequests.length > 0 && selectedRequests.length === filteredRequests.length}
                />
                <label htmlFor="selectAll">Select All</label>
              </div>
            </div>

            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Select</th>
                    <th>Applied On</th>
                    <th>For Date</th>
                    <th>In Time</th>
                    <th>Out Time</th>
                    <th>Reason</th>
                    <th>Request By</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.length > 0 ? (
                    filteredRequests.map(request => (
                      <tr key={request.id}>
                        <td>
                          <input
                            type="checkbox"
                            onChange={e => handleSelectRequest(e, request.id)}
                            checked={selectedRequests.includes(request.id)}
                          />
                        </td>
                        <td>{moment(request.created_at).format('MMM DD, YYYY HH:mm')}</td>
                        <td>{moment(request.in_date).format('MMM DD, YYYY')}</td>
                        <td>{request.in_time ? moment(request.in_time, 'HH:mm:ss').format('hh:mm A') : '—'}</td>
                        <td>{request.out_time ? moment(request.out_time, 'HH:mm:ss').format('hh:mm A') : '—'}</td>
                        <td>{request.reason || '—'}</td>
                        <td>{request.user_name} ({request.employee_code})</td>
                        <td>
                          <span 
                            className={`status-badge ${request.status} clickable`}
                            onClick={() => navigate(`/ar-decision/${request.id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {request.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="empty-state">No requests found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bulk-actions">
              <button 
                className="action-btn approve" 
                onClick={() => handleBulkAction('approve')} 
                disabled={selectedRequests.length === 0}
              >
                ✓ Approve ({selectedRequests.length})
              </button>
              <button 
                className="action-btn reject" 
                onClick={() => handleBulkAction('reject')} 
                disabled={selectedRequests.length === 0}
              >
                ✕ Reject ({selectedRequests.length})
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AllRequests;
